import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_STATS = { total: 0, new: 0, contacted: 0, converted: 0 };

// Wraps the admin-leads data lifecycle: fetch, status change, delete.
// Handles 401 by calling onLogout so the caller doesn't repeat that plumbing.
export const useAdminLeads = (token, onLogout) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const fetchLeads = useCallback(async (showSpinner = true) => {
    if (showSpinner) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/admin/leads`, authHeader);
      setLeads(response.data.leads || []);
      setStats(response.data.stats || EMPTY_STATS);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => { fetchLeads(false); }, [fetchLeads]);

  const updateStatus = useCallback(async (leadId, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { status }, authHeader);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
      fetchLeads(false);
      return { id: leadId, status };
    } catch {
      toast.error("Failed to update status");
      return null;
    }
  }, [authHeader, fetchLeads]);

  const deleteLead = useCallback(async (leadId) => {
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, authHeader);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      toast.success("Lead deleted");
      fetchLeads(false);
      return true;
    } catch {
      toast.error("Failed to delete");
      return false;
    }
  }, [authHeader, fetchLeads]);

  return { leads, stats, loading, refreshing, fetchLeads, updateStatus, deleteLead };
};
